import { ChevronLeft, Minus, UserRoundPlus } from "lucide-react"
import AuthLayout from "../../../components/layouts/auth"
import { Link, useParams } from "react-router-dom"
import InputField from "../../../components/form/input-field"
import RadioField from "../../../components/form/radio-field"
import TextareaField from "../../../components/form/textarea"
import { useEffect, useState } from "react"
import ToggleSwitch from "../../../components/form/toggle-switch"
import ApiRequest from "../../../lib/ApiCall"
import NotFound from "../../../components/not-found"
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup'
import SpinnerLoader from "../../../components/loader/spinner-loader"
import { ErrorFlash, SuccessFlash } from "../../../lib/flash-notification"
import Tooltip from "../../../components/tooltip"

const EditMember = () => {
    const { id } = useParams<{ id: string }>();

    type MemberType = {
        uuid: string,
        contactRepresentatives: representativesType[],
        secRegistrationNumber: string,
        websiteUrl: string,
        status: string,
        logo: string,
        nameInstitution: string,
        registeredAddress: string,
        contactAddress: string,
    };

    const [member, setMember] = useState<MemberType>({} as MemberType);
    const [pageLoading, setPageLoading] = useState(true);

    const getMember = async () => {
        const request = await ApiRequest({ endpoint: `/member/${id}`, method: 'GET' });
        setPageLoading(false);
        if (request.status !== '200') return;
        setMember(request.data);
        reset(request.data);
        setSameAddress(request.data.sameAddress);
        setRepresentatives(request.data.contactRepresentatives || [{} as representativesType]);
    }

    useEffect(() => { getMember() }, []);

    type representativesType = {
        name: string,
        position: string
        mobile_number: string,
        email_address: string,
        is_primary: boolean
    }

    const [representatives, setRepresentatives] = useState<representativesType[]>([{} as representativesType]);
    const [sameAddress, setSameAddress] = useState<boolean>(false);

    const removeRepresentative = (index: number) => {
        const newRepresentatives = [...representatives];
        newRepresentatives.splice(index, 1);
        setRepresentatives(newRepresentatives);
    };

    const onChangeRepresentativeInput = (index: number, field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const newRepresentatives = representatives.map((rep, i) => {
            if (i === index) {
                const target = e.target as HTMLInputElement | HTMLTextAreaElement;
                return { ...rep, [field]: target.value };
            }
            return rep;
        });
        setRepresentatives(newRepresentatives);
        clearErrors('contactRepresentatives'); // FIXED: Clear errors properly
    };

    const onPrimaryContact = (index: number) => {
        const newRepresentatives = representatives.map((rep, i) => {
            return { ...rep, is_primary: i === index };
        });
        setRepresentatives(newRepresentatives);
    }

    const formSchema = yup.object({
        nameInstitution: yup.string(),
        registeredAddress: yup.string().nullable(),
        websiteUrl: yup.string().required('Website Url is required'),
        contactAddress: yup.string().nullable(),
        secRegistrationNumber: yup.string().nullable(),
        contactRepresentatives: yup.array().of(
            yup.object({
                name: yup.string(),
                position: yup.string(),
                mobile_number: yup.string(),
                email_address: yup.string(),
                is_primary: yup.boolean()
            })
        )
    });

    const {
        setValue,
        setError,
        formState: { errors },
        handleSubmit,
        getValues,
        reset,
        clearErrors,
    } = useForm({
        resolver: yupResolver(formSchema),
        // FIXED: Stop RHF from validating on every keystroke
        mode: 'onSubmit',
        reValidateMode: 'onSubmit'
    });

    // FIXED: Keep react-hook-form in sync with your local representatives state
    // so Yup validation has the correct data when you click submit.
    useEffect(() => {
        setValue('contactRepresentatives', representatives);
    }, [representatives, setValue]);

    const onRegisteredAddress = (value: string) => {
        sameAddress && setValue('contactAddress', value);
    }

    const onSameAddress = (state: boolean) => {
        setSameAddress(state);
        setValue('contactAddress', getValues('registeredAddress'));
        clearErrors('contactAddress');
    }

    const convertToBase64 = async (file: any) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result.replace("data:", "").replace(/^.+,/, ""));
                } else {
                    reject(new Error('Invalid file type'));
                }
            }
            reader.onerror = (error) => reject(error);
        });
    }

    const getFileSizeInMB = (file: any): number => {
        return parseInt((file.size / (1024 * 1024)).toFixed(2));
    }

    const onLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        const base64 = await convertToBase64(file);
        const size = getFileSizeInMB(file);
        if (size > 2) return;

        setLoadingSubmit(true);
        const request = await ApiRequest({
            endpoint: '/member/upload-logo', method: 'POST', payload: {
                imageInBase64: base64,
                memberId: member?.uuid,
                imageExtension: file?.type.split('/')[1]
            }
        });

        event.target.value = "";
        setLoadingSubmit(false);
        if (request.status === '200') {
            SuccessFlash("Logo uploaded successfully");
            setMember({ ...member, logo: request.data.logo });
            return;
        }
    }

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const updateMember = async (data: any) => {
        data['contactRepresentatives'] = representatives;
        data['memberId'] = id;

        setLoadingSubmit(true);
        const request = await ApiRequest({ endpoint: 'member/update', method: 'POST', payload: data });
        setLoadingSubmit(false);

        if (request.status === '200') {
            setMember(request.data);
            clearErrors();
            SuccessFlash('Member updated successfully');
            return;
        }

        ErrorFlash(request.message);

        if (request.status === "payloadValidationError") {
            for (const [field, messages] of Object.entries(request.data)) {
                const input_field: any = field;
                const error_message: any = messages
                setError(input_field, { type: "manual", message: error_message.join(", ") });
            }
        }
    }

    return (
        <AuthLayout>
            <div>
                {(!pageLoading && !member.uuid) && <NotFound route="/members" />}

                {member.uuid &&
                    <div>
                        <div className="relative group bg-[#F8F8F8] mb-6 flex justify-center items-center rounded-lg lg:w-[250px] h-[120px] overflow-hidden border border-gray-200">
                            {member.logo && (
                                <img src={member.logo} alt="logo" className="absolute inset-0 w-full h-full object-contain p-4" />
                            )}
                            <div className={`absolute inset-0 flex flex-col items-center justify-center bg-black/50 transition-all duration-300 ${loadingSubmit ? "pointer-events-none" : ""}`}>
                                <span className="text-white text-sm font-medium transition">
                                    {member.logo ? "Change Logo" : "Upload Logo"}
                                </span>
                                <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp" onChange={onLogoUpload} disabled={loadingSubmit} className="absolute w-full h-full opacity-0 cursor-pointer" />
                            </div>
                            {loadingSubmit && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                                    <div className="flex flex-col items-center text-white text-sm">
                                        <SpinnerLoader />
                                        <span className="mt-2">Uploading...</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSubmit(updateMember, (errors) => console.log("VALIDATION ERRORS:", errors))}>
                            <div className="items-center justify-between space-y-5 lg:flex lg:space-y-0">
                                <div className="flex items-center space-x-2">
                                    <Link to={`/members/${id}`}><ChevronLeft /></Link>
                                    <h1 className="text-sm page-title">Edit: {member.nameInstitution}</h1>
                                </div>
                                <button disabled={loadingSubmit} className={`px-10 pryBtn w-fit ${loadingSubmit && 'opacity-[0.5]'}`}>
                                    <span>Update member {loadingSubmit && <SpinnerLoader />}</span>
                                </button>
                            </div>

                            <div className="mt-10">
                                <div>
                                    <div className="grid gap-4 lg:grid-cols-2">
                                        <div>
                                            <InputField
                                                error={errors.nameInstitution?.message}
                                                // FIXED: Changed array syntax to standard {} and used clearErrors
                                                onChangeInput={(e) => {
                                                    setValue("nameInstitution", e.target.value);
                                                    clearErrors("nameInstitution");
                                                }}
                                                value={getValues('nameInstitution') || ''}
                                                label="Institution Name"
                                            />
                                        </div>
                                        <div>
                                            <InputField
                                                error={errors.secRegistrationNumber?.message}
                                                onChangeInput={(e) => {
                                                    setValue("secRegistrationNumber", e.target.value);
                                                    clearErrors("secRegistrationNumber");
                                                }}
                                                value={getValues("secRegistrationNumber") || ''}
                                                label="SEC Registration Number"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="mt-4">
                                            <InputField
                                                error={errors.websiteUrl?.message}
                                                onChangeInput={(e) => {
                                                    setValue("websiteUrl", e.target.value);
                                                    clearErrors("websiteUrl");
                                                }}
                                                value={getValues("websiteUrl") || ''}
                                                label="Website URL"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <TextareaField
                                            label='Registered Address'
                                            placeholder='Enter full address'
                                            error={errors.registeredAddress?.message}
                                            onChangeInput={(e) => {
                                                setValue('registeredAddress', e.target.value);
                                                onRegisteredAddress(e.target.value);
                                                clearErrors('registeredAddress');
                                            }}
                                            value={getValues('registeredAddress') || ''}
                                        />
                                    </div>

                                    <div className="flex mt-4 space-x-3">
                                        <ToggleSwitch
                                            initialState={sameAddress}
                                            onToggle={(state) => onSameAddress(state)}
                                        />
                                        <span className="label">Contact Address is the same as above</span>
                                    </div>

                                    <div className={`${sameAddress ? 'hidden' : ''} mb-10 mt-4`}>
                                        <TextareaField
                                            label='Contact Address'
                                            placeholder='Enter full address'
                                            error={errors.contactAddress?.message}
                                            value={getValues('contactAddress') || ''}
                                            onChangeInput={(e) => {
                                                setValue('contactAddress', e.target.value);
                                                clearErrors('contactAddress');
                                            }}
                                        />
                                    </div>

                                    <div className="mt-4">
                                        <p className="font-bold label text-secondary">Contact details of Representatives</p>
                                    </div>

                                    {representatives.map((rep, index) => (
                                        <div key={index} className="mt-4">
                                            <p className="font-bold label">
                                                Contact {index + 1}:
                                                {index > 0 && ( // Changed to index > 0 so you don't delete the first one
                                                    <span onClick={() => removeRepresentative(index)} className="cursor-pointer text-red-800 text-sm flex items-center space-x-1 font-[400] hover:underline w-fit">
                                                        <Minus size={18} />
                                                        <span>Remove Representative</span>
                                                    </span>
                                                )}
                                            </p>
                                            <div className="grid gap-4 mt-1 lg:grid-cols-2">
                                                <div>
                                                    <InputField
                                                        label="Name"
                                                        onChangeInput={(e) => onChangeRepresentativeInput(index, 'name', e)}
                                                        value={rep.name || ''}
                                                        placeholder="Enter first and last name"
                                                        error={errors.contactRepresentatives?.[index]?.name?.message}
                                                    />
                                                </div>
                                                <div>
                                                    <InputField
                                                        label="Position"
                                                        onChangeInput={(e) => onChangeRepresentativeInput(index, 'position', e)}
                                                        value={rep.position || ''}
                                                        placeholder="Enter job title/position"
                                                        error={errors.contactRepresentatives?.[index]?.position?.message}
                                                    />
                                                </div>
                                                <div>
                                                    <InputField
                                                        label="Mobile number"
                                                        onChangeInput={(e) => onChangeRepresentativeInput(index, 'mobile_number', e)}
                                                        type="number"
                                                        value={rep.mobile_number || ''}
                                                        placeholder="E.g. +2348031231234"
                                                        error={errors.contactRepresentatives?.[index]?.mobile_number?.message}
                                                    />
                                                </div>
                                                <div>
                                                    <InputField
                                                        label="Email address"
                                                        onChangeInput={(e) => onChangeRepresentativeInput(index, 'email_address', e)}
                                                        type="email"
                                                        value={rep.email_address || ''}
                                                        placeholder="name@company.com"
                                                        error={errors.contactRepresentatives?.[index]?.email_address?.message}
                                                    />
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <RadioField
                                                        name="primary_contact"
                                                        onChangeInput={() => onPrimaryContact(index)}
                                                        label="Set as primary contact"
                                                        checked={rep.is_primary}
                                                        value={index.toString()}
                                                    />
                                                    <Tooltip text="The portal account will be created with the email address of the primary contact" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <div className="mt-4">
                                        <button type="button" onClick={() => setRepresentatives([...representatives, {} as representativesType])} className={`pryBtnOutline w-fit`}>
                                            <UserRoundPlus size={18} />
                                            <span>Add new representative</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                }
            </div>
        </AuthLayout>
    )
}

export default EditMember